/**
 * Points (Discover Pakistan) API client.
 * List and retrieve points; categories remain on hotelService.getPointCategories().
 */
import api from './api/axiosConfig';

const appendPointFormFields = (formData, key, val) => {
  if (val === undefined || val === null || val === '') return;
  if (key === 'display_image_index' && val !== undefined && val !== null) {
    formData.append(key, String(val));
    return;
  }
  formData.append(key, val);
};

const buildPointFormData = (pointData) => {
  const formData = new FormData();
  Object.keys(pointData).forEach((key) => {
    if (key === 'images' && Array.isArray(pointData.images)) {
      pointData.images.forEach((image) => formData.append('images', image));
      return;
    }
    if (key === 'images_to_delete' && Array.isArray(pointData.images_to_delete)) {
      pointData.images_to_delete.forEach((id) => formData.append('images_to_delete', id));
      return;
    }
    appendPointFormFields(formData, key, pointData[key]);
  });
  return formData;
};

const pointService = {
  /**
   * Get points with optional filters.
   * @param {Object} params - page, page_size, point_category, point_category_id, search, ordering
   * @returns {Promise<{ count: number, results: Array }>}
   */
  getPoints: async (params = {}) => {
    const response = await api.get('/points/', { params });
    return response.data;
  },

  /**
   * Get a single point by slug.
   * @param {string} slug - Point slug
   */
  getPoint: async (slug) => {
    const response = await api.get(`/points/${slug}/`);
    return response.data;
  },

  /**
   * Create a point (multipart when images are included).
   * @param {FormData|Object} data - FormData or plain object with optional images[]
   */
  createPoint: async (data) => {
    const formData = data instanceof FormData ? data : buildPointFormData(data);
    const response = await api.post('/points/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  /**
   * Update a point (multipart for new images / cover index).
   * @param {string} slug
   * @param {Object} data
   */
  updatePoint: async (slug, data) => {
    const formData = buildPointFormData(data);
    const response = await api.patch(`/points/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  /**
   * Get reviews (comments) for a point.
   * @param {string} slug - Point slug
   */
  getPointReviews: async (slug) => {
    const response = await api.get(`/points/${slug}/reviews/`);
    return response.data;
  },

  /**
   * Create a review/comment for a point. Requires authentication.
   * @param {string} slug - Point slug
   * @param {Object} data - { rating, title?, comment? }
   */
  createPointReview: async (slug, data) => {
    const response = await api.post(`/points/${slug}/reviews/`, data);
    return response.data;
  },

  getRegions: async () => {
    const response = await api.get('/points/regions/');
    return response.data?.results ?? response.data ?? [];
  },

  getSeasons: async () => {
    const response = await api.get('/points/seasons/');
    return response.data?.results ?? response.data ?? [];
  },

  getCulturalHighlights: async () => {
    const response = await api.get('/points/cultural-highlights/');
    return response.data?.results ?? response.data ?? [];
  },

  getGalleryImages: async () => {
    const response = await api.get('/points/gallery-images/');
    return response.data?.results ?? response.data ?? [];
  },

  getTravelTips: async () => {
    const response = await api.get('/points/travel-tips/');
    return response.data?.results ?? response.data ?? [];
  },

  getSeasonDetail: async () => {
    try {
      const response = await api.get('/points/season-detail/');
      return response.data;
    } catch {
      return null;
    }
  },
};

export default pointService;
